import type { AdDestinationKey, AdSegment, CampaignPauseReason, CampaignStatusKey, Targeting } from '@alicerce/contracts';

export interface Creative {
  id: string;
  productId: string | null;
  headline: string;
  primaryText: string;
  imageUrls: string[];
  status: string;
}

export interface Campaign {
  id: string;
  name: string;
  templateKey: string | null;
  segment: string | null;
  platform: string;
  objective: string;
  destination: AdDestinationKey;
  status: CampaignStatusKey;
  dailyBudgetCents: number;
  totalBudgetCents: number | null;
  targeting: Targeting;
  startsAt: string;
  endsAt: string | null;
  durationDays: number | null;
  reviewComment: string | null;
  rejectionReason: string | null;
  pausedReason: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const GENDER_LABEL = { all: 'Todos', female: 'Mulheres', male: 'Homens' } as const;

/** "Mulheres, 25–45 anos, até 10 km de Rua X · Interesses: moda, beleza". */
export function targetingSummary(t: Targeting) {
  const where = t.location.kind === 'radius' ? `até ${t.location.radiusKm} km de ${t.location.address}` : t.location.cities.map((c) => c.name).join(', ');
  const parts = [`${GENDER_LABEL[t.genders]}, ${t.ageMin}–${t.ageMax} anos`, where];
  if (t.interests.length) parts.push(`Interesses: ${t.interests.map((i) => i.name).join(', ')}`);
  return parts.join(' · ');
}

export const CAMPAIGN_STATUS: Record<CampaignStatusKey, { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' }> = {
  DRAFT: { label: 'Rascunho', tone: 'neutral' },
  PENDING_REVIEW: { label: 'Em revisão', tone: 'info' },
  SUBMITTED: { label: 'Em análise na Meta', tone: 'info' },
  ACTIVE: { label: 'No ar', tone: 'success' },
  PAUSED: { label: 'Pausada', tone: 'warning' },
  FINISHED: { label: 'Encerrada', tone: 'neutral' },
  REJECTED: { label: 'Reprovada', tone: 'danger' },
};

export const DESTINATION_LABEL: Record<AdDestinationKey, string> = { STOREFRONT: 'Loja online', WHATSAPP: 'WhatsApp' };

export const SEGMENT_LABEL: Record<AdSegment, string> = {
  moda: 'Moda e acessórios',
  beleza: 'Beleza e cuidados',
  alimentacao: 'Comida e bebida',
  servicos: 'Serviços',
  outros: 'Outro tipo de negócio',
};

/** O que conta como resultado, conforme o destino. */
export const RESULT_LABEL: Record<AdDestinationKey, { plural: string; cost: string }> = {
  STOREFRONT: { plural: 'Vendas', cost: 'Custo por venda' },
  WHATSAPP: { plural: 'Conversas', cost: 'Custo por conversa' },
};

export const PAUSE_REASON_LABEL: Record<CampaignPauseReason, string> = {
  MERCHANT: 'Você pausou esta campanha.',
  NO_CREDITS: 'A campanha foi pausada porque os créditos de anúncio acabaram. Compre créditos e retome.',
  MODULE_DISABLED: 'A campanha foi pausada porque o módulo de anúncios foi desativado.',
};

/** Ações do lojista por status (as mesmas regras da API). */
export const CAMPAIGN_ACTIONS: Record<'edit' | 'submit' | 'pause' | 'resume' | 'finish', readonly CampaignStatusKey[]> = {
  edit: ['DRAFT', 'PENDING_REVIEW', 'REJECTED'],
  submit: ['DRAFT', 'REJECTED'],
  pause: ['ACTIVE'],
  resume: ['PAUSED'],
  finish: ['DRAFT', 'PENDING_REVIEW', 'SUBMITTED', 'ACTIVE', 'PAUSED', 'REJECTED'],
};
